/**
 * `useWorkspacePinState` — the generic per-tab pin state hook (Task 18).
 *
 * Derives one of three states for a workspace tab's pin affordance:
 *  - `disabled` — the tab has no connection/database, so there's nothing to
 *    scope a pin to (F12). The `reason` doubles as the button's tooltip.
 *  - `unpinned` — an eligible tab that isn't linked to any pin.
 *  - `linked`   — the tab was opened from (or just created) a pin; carries the
 *    live pin ref and a `diverged` flag.
 *
 * Divergence (F5/F2/G1) is computed by pulling the LIVE tab definition from the
 * pin-capture registry (`capturePinDefinition`) and comparing it against the
 * pin's stored payload with `isPinDiverged`. It is recomputed once on mount and
 * again on each re-render of the owning button — opening the button's menu
 * re-renders it, which satisfies the "recompute on menu-open + once on mount"
 * rule WITHOUT any polling/interval: this hook deliberately subscribes only to
 * the tab's `sourcePin` + connection identity (never its editor content), so a
 * keystroke in the query/notebook does not re-run divergence.
 *
 * G5 severing: the pin is resolved from `byConnection[sourcePin.connectionId]`.
 *  - slot ABSENT (undefined) ⇒ the connection isn't hydrated yet ⇒ treat as
 *    "unknown": KEEP the link (bridged by the last-known pin) and never sever.
 *  - slot PRESENT but its pins lack the id ⇒ the pin was genuinely removed
 *    (another window / this window) ⇒ sever the tab's `sourcePin`.
 *
 * Severing depends on the slot actually loading. `DatabaseExplorer` hydrates
 * EVERY active connection (see `DatabaseExplorer.tsx` → `usePinnedObjectsStore
 * .hydrate` over `activeConnections`), which subscribes the `onChange` listener
 * and populates the slot. A linked tab's `sourcePin.connectionId` is that tab's
 * own (active) connection, so its slot is present and severing fires when the
 * pin disappears. When the connection is NOT active (disconnected) the slot
 * stays absent and we intentionally keep the link — we cannot confirm removal
 * on a connection we can't read.
 *
 * Two Codex gate-3 corrections layer on top of the three modes above:
 *  - Cold-restore `disabled` (gate-3 finding F5) — a linked tab whose slot has
 *    never hydrated THIS session (`slot === undefined`) and has no
 *    last-known pin to bridge with is UNKNOWN, not "gone". Reporting it as
 *    `unpinned` let a click before hydration mint a duplicate pin for an
 *    already-pinned tab; it now reports `disabled` (`PIN_DISABLED_RESOLVING`)
 *    until the slot settles one way or the other.
 *  - Non-capturable `disabled` (gate-3 finding F3) — an eligible, non-linked
 *    tab whose registered capture returns `null` (blueprint-edit / row-context
 *    Table Explorer) now reports `disabled` (`PIN_DISABLED_NOT_CAPTURABLE`)
 *    instead of `unpinned`, so the tab-strip context menu (which has no
 *    surface-specific `disabledReason` override, unlike the toolbar's
 *    `WorkspacePinButton`) can't offer a prohibited or silently-no-op pin.
 */

import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { usePinnedObjectsStore } from '../../../database-objects/store/pinnedObjectsStore';
import { isWorkspacePinRef } from '../../../database-objects/services/pinnedObjects/pinRef';
import { isPinDiverged } from '../../../database-objects/services/pinnedObjects/pinDivergence';
import {
  capturePinDefinition,
  subscribePinCaptures,
  getPinCaptureVersion,
} from '../services/pinCaptureRegistry';
import {
  comparePinSourceBlock,
  isComparePinTab,
  pinScopeForTab,
  type ComparePinSourceBlock,
} from '../services/comparePin';
import type { PinnedObjectRef, PinnedWorkspaceRef } from '../../../../types/electron';

export type WorkspacePinState =
  | { mode: 'disabled'; reason: string }
  | { mode: 'unpinned' }
  | { mode: 'linked'; pin: PinnedWorkspaceRef; diverged: boolean };

/** Shown when a tab isn't bound to a connection/database (F12). */
export const PIN_DISABLED_NO_CONNECTION =
  'Connect to a database to pin this query/notebook';

/**
 * Shown when a sourcePin-linked tab's pin slot hasn't hydrated yet this
 * session (cold restore) — the link is neither confirmed nor severed, so
 * Create must be blocked rather than briefly offering a duplicate-pin click
 * (F5, Codex gate-3).
 */
export const PIN_DISABLED_RESOLVING = 'Resolving pinned view…';

/**
 * Shown for an otherwise-eligible, non-linked tab whose registered capture
 * returns `null` (blueprint-edit / row-context Table Explorer, or no capture
 * registered yet) — the tab menu must disable the action instead of silently
 * no-opping on click (F3, Codex gate-3).
 */
export const PIN_DISABLED_NOT_CAPTURABLE = "This view can't be pinned";

/**
 * Shown for a DATA-compare tab whose source isn't picked yet (B5).
 * `PIN_DISABLED_NO_CONNECTION`'s "connect to a database" wording would be
 * misleading here: the tab may be fully connected on the target side.
 *
 * W5.1c fix round 1 split this constant three ways. It used to cover the
 * schema-compare and blueprint-folder cases too, and said "database and table"
 * in all of them — but Schema Compare has no table concept, and a folder source
 * isn't a missing selection at all. That was tolerable while the only surface
 * was a context-menu item; the pin control now sits permanently on the Schema
 * Compare header, so the wrong sentence is on screen the whole time.
 */
export const PIN_DISABLED_NO_COMPARE_SOURCE =
  'Choose a source database and table before pinning this comparison';

/** Schema Compare's source is a database — no table is involved. */
export const PIN_DISABLED_NO_COMPARE_SOURCE_DATABASE =
  'Choose a source database before pinning this comparison';

/**
 * A Database Blueprint folder source. Structural, not one-more-click: pins are
 * filed under a connection in `connectionMetaInfo`, and a folder has none. Say
 * so plainly instead of asking the user to pick something they already picked.
 */
export const PIN_DISABLED_COMPARE_FOLDER_SOURCE =
  "A comparison from a Database Blueprint folder can't be pinned — a pin is saved under its source connection";

/**
 * Shown for a compare tab whose source is fine but whose TARGET isn't chosen
 * yet (B5). The generic `PIN_DISABLED_NOT_CAPTURABLE` ("This view can't be
 * pinned") reads as a dead end for what is really a one-step-away state —
 * half a comparison isn't a comparison, but the user is one picker away from
 * a pinnable one.
 */
export const PIN_DISABLED_NO_COMPARE_TARGET =
  'Choose a target to compare against before pinning this comparison';

/**
 * Divergence for a linked pin. Pulls the LIVE tab definition from the capture
 * registry; a missing capture (no capture registered yet, or the callback
 * threw) is reported as "not diverged" rather than a false amber dot.
 */
function computeDiverged(
  pin: PinnedWorkspaceRef,
  tabId: string,
  tabDatabase: string | undefined,
): boolean {
  const captured = capturePinDefinition(tabId);
  if (!captured) return false;
  return isPinDiverged(pin, captured, tabDatabase);
}

/**
 * Maps the classified source block to the sentence the user reads. A `null`
 * block on a compare tab means the source is fine but something else (a
 * disconnected connection) tripped the F12 eligibility check — the data-compare
 * wording is the historical default there and stays.
 */
function compareSourceReason(block: ComparePinSourceBlock | null): string {
  switch (block) {
    case 'folder':
      return PIN_DISABLED_COMPARE_FOLDER_SOURCE;
    case 'unconfigured-schema':
      return PIN_DISABLED_NO_COMPARE_SOURCE_DATABASE;
    default:
      return PIN_DISABLED_NO_COMPARE_SOURCE;
  }
}

export function useWorkspacePinState(tabId: string): WorkspacePinState {
  // Re-renders this hook whenever any tab registers/unregisters its pin
  // capture (Codex gate-3 F3-followup). Tab components register from a
  // `useEffect`, which runs AFTER first render, so without this subscription
  // a freshly-mounted tab's `capturePinDefinition(tabId) === null` check
  // below would evaluate once against "not yet registered" and never
  // recompute — leaving the pin affordance stuck on `disabled` until some
  // unrelated re-render happened to occur.
  useSyncExternalStore(subscribePinCaptures, getPinCaptureVersion);

  const sourcePin = useWorkspaceStore((s) => s.tabs.find((t) => t.id === tabId)?.sourcePin);
  // B5 — the pin's scope comes from `pinScopeForTab`, not `connectionInfo`
  // directly: a comparison is scoped to its SOURCE endpoint. Both selectors
  // return primitives, so the subscription stays as narrow as it was.
  const connectionId = useWorkspaceStore(
    (s) => pinScopeForTab(s.tabs.find((t) => t.id === tabId)).connectionId,
  );
  const database = useWorkspaceStore(
    (s) => pinScopeForTab(s.tabs.find((t) => t.id === tabId)).database,
  );
  const isCompareTab = useWorkspaceStore((s) => isComparePinTab(s.tabs.find((t) => t.id === tabId)));
  // Which of the three "source can't anchor a pin" situations this is, so the
  // disabled tooltip can be true rather than merely present. A string-literal
  // union, so the subscription stays primitive like the ones above.
  const compareSourceBlock = useWorkspaceStore((s) =>
    comparePinSourceBlock(s.tabs.find((t) => t.id === tabId)),
  );
  const setSourcePin = useWorkspaceStore((s) => s.setSourcePin);

  // Subscribe to the pin slot for the sourcePin's connection. When there's no
  // sourcePin the key is '' — `byConnection['']` is always undefined, an inert
  // subscription that never re-renders.
  const slotConnectionId = sourcePin?.connectionId ?? '';
  const slot = usePinnedObjectsStore((s) => s.byConnection[slotConnectionId]);

  // The pin resolved from the CURRENT store snapshot, if present.
  const resolvedPin = useMemo<PinnedWorkspaceRef | null>(() => {
    if (!sourcePin || !slot) return null;
    const found = slot.pinnedObjects.find(
      (r: PinnedObjectRef) => isWorkspacePinRef(r) && r.id === sourcePin.pinId,
    );
    return (found as PinnedWorkspaceRef | undefined) ?? null;
  }, [sourcePin, slot]);

  // Last-known pin — bridges the brief window where the slot is momentarily
  // absent (connection re-hydrating) so the link doesn't flicker off. Reset
  // whenever the link identity changes so a new link never inherits a stale pin.
  // `\x00` as an ESCAPE, never a literal NUL byte. A raw NUL makes git
  // classify this whole file as BINARY: `0714f6330` rendered it as
  // `Bin 9772 -> 10691 bytes`, 0 insertions / 0 deletions, so its diff was
  // invisible to review and 3-way merge degrades to whole-file conflict.
  // Same defect that was cleaned out of `schemaComparer.ts`. The runtime
  // string is byte-identical; only the source encoding changes.
  const linkKey = sourcePin ? `${sourcePin.connectionId}\x00${sourcePin.pinId}` : '';
  const lastKeyRef = useRef(linkKey);
  const lastKnownPinRef = useRef<PinnedWorkspaceRef | null>(null);
  if (lastKeyRef.current !== linkKey) {
    lastKeyRef.current = linkKey;
    lastKnownPinRef.current = null;
  }
  if (resolvedPin) lastKnownPinRef.current = resolvedPin;

  // Sever when the pin is confirmed GONE from a hydrated snapshot (G5). Only
  // fires when the slot is present — an absent slot is "unknown", never a sever.
  const shouldSever = !!sourcePin && slot !== undefined && !resolvedPin;
  useEffect(() => {
    if (shouldSever && sourcePin) {
      setSourcePin(tabId, undefined);
    }
  }, [shouldSever, sourcePin, tabId, setSourcePin]);

  // Eligibility (F12) — checked before link state so a disconnected tab reads
  // as "connect first" rather than dangling in a link it can't act on.
  if (!connectionId || !database) {
    return {
      mode: 'disabled',
      reason: isCompareTab
        ? compareSourceReason(compareSourceBlock)
        : PIN_DISABLED_NO_CONNECTION,
    };
  }

  if (sourcePin) {
    // Prefer the freshly-resolved pin; fall back to the last-known one only
    // while the slot is UNKNOWN (absent). A present-but-empty slot means the
    // pin is gone — surface as unpinned (the effect above severs the link).
    const linkedPin = resolvedPin ?? (slot === undefined ? lastKnownPinRef.current : null);
    if (linkedPin) {
      return { mode: 'linked', pin: linkedPin, diverged: computeDiverged(linkedPin, tabId, database) };
    }
    // F5 — cold restore: sourcePin is set but the slot has never hydrated
    // this session (slot undefined) and there's no last-known pin to bridge
    // with. This is UNKNOWN, not "gone" — surfacing as unpinned here would
    // let a click before hydration mint a duplicate pin for an already-pinned
    // tab. Only a HYDRATED-and-empty slot (checked below) means the pin was
    // genuinely removed.
    if (slot === undefined) {
      return { mode: 'disabled', reason: PIN_DISABLED_RESOLVING };
    }
    return { mode: 'unpinned' };
  }

  // F3 — an eligible (passed F12), non-linked tab whose registered capture
  // returns null (blueprint-edit / row-context Table Explorer, or no capture
  // registered yet) can't actually be pinned. Disable rather than silently
  // no-op on click. Cheap: a Map.get + a getState-based callback, and this
  // hook only re-renders on its narrow subscriptions (not per keystroke).
  if (capturePinDefinition(tabId) !== null) return { mode: 'unpinned' };
  // B5 — a compare tab that got this far has a usable SOURCE (the F12 check
  // above passed via `pinScopeForTab`), so once its capture is registered the
  // only thing that can still make it return null is a missing TARGET. Say
  // that instead of the generic "can't be pinned". (Before the tab's effect
  // registers, this reads the same way for one frame — the registry
  // subscription at the top re-renders us the moment it lands, exactly as it
  // does for the other pinnable types.)
  return {
    mode: 'disabled',
    reason: isCompareTab ? PIN_DISABLED_NO_COMPARE_TARGET : PIN_DISABLED_NOT_CAPTURABLE,
  };
}
